import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FolderPlus } from "lucide-react";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import styles from "./CreateProjectPage.module.css";
import ClientProjectForm from "../../components/Layout/ServiceForms/ClientProjectForm";
import PortalSubscriptionForm from "../../components/Layout/ServiceForms/PortalSubscriptionForm";
import AddDomainForm from "../../components/Layout/ServiceForms/AddDomainForm";
import UpdateProjectForm from "../../components/Layout/ServiceForms/UpdateProjectForm";
import DisableProjectForm from "../../components/Layout/ServiceForms/DisableProjectForm";
import DeleteProjectForm from "../../components/Layout/ServiceForms/DeleteProjectForm";
import UpdateSubscriptionPlanForm from "../../components/Layout/ServiceForms/UpdateSubscriptionPlanForm";
import DisableSubscriptionPlanForm from "../../components/Layout/ServiceForms/DisableSubscriptionPlanForm";
import DeleteSubscriptionPlanForm from "../../components/Layout/ServiceForms/DeleteSubscriptionPlanForm";

export default function CreateProjectPage() {
  const [activeTab, setActiveTab] = useState("client");
  const [activeInnerTab, setActiveInnerTab] = useState("create");
  const [activePortalInnerTab, setActivePortalInnerTab] = useState("create");
  const { t } = useTranslation("common");

  return (
    <div className={portalStyles.portalPage}>
      <div className={portalStyles.container}>
        <PortalShellPageHeader
          icon={
            <FolderPlus size={28} strokeWidth={1.75} absoluteStrokeWidth />
          }
          title={t("createProjectPage.title")}
          subtitle={t("createProjectPage.subtitle")}
        />

        <div className={styles.main}>
          <div className={styles.tabNavigation}>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "client" ? styles.activeTab : ""
              }`}
              onClick={() => {
                setActiveTab("client");
                setActiveInnerTab("create");
              }}
            >
              {t("createProjectPage.tabs.softindexClient")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "portal" ? styles.activeTab : ""
              }`}
              onClick={() => {
                setActiveTab("portal");
                setActivePortalInnerTab("create");
              }}
            >
              {t("createProjectPage.tabs.softindexPortal")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "domain" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("domain")}
            >
              {t("createProjectPage.tabs.addDomain")}
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === "client" && (
              <>
                <div className={styles.innerTabNavigation}>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activeInnerTab === "create" ? styles.activeInnerTab : ""
                    }`}
                    onClick={() => setActiveInnerTab("create")}
                  >
                    Create Project
                  </button>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activeInnerTab === "update" ? styles.activeInnerTab : ""
                    }`}
                    onClick={() => setActiveInnerTab("update")}
                  >
                    Update Project
                  </button>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activeInnerTab === "disable" ? styles.activeInnerTab : ""
                    }`}
                    onClick={() => setActiveInnerTab("disable")}
                  >
                    Disable Project
                  </button>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activeInnerTab === "delete" ? styles.activeInnerTab : ""
                    }`}
                    onClick={() => setActiveInnerTab("delete")}
                  >
                    Delete Project
                  </button>
                </div>

                {activeInnerTab === "create" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>
                        {t("createProjectPage.sections.clientProject.title")}
                      </h2>
                      <p className={styles.sectionDescription}>
                        {t(
                          "createProjectPage.sections.clientProject.description"
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {activeInnerTab === "update" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>Update Project</h2>
                      <p className={styles.sectionDescription}>
                        Select and update an existing project
                      </p>
                    </div>
                  </div>
                )}

                {activeInnerTab === "disable" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>
                        Disable / Enable Project
                      </h2>
                      <p className={styles.sectionDescription}>
                        Select a project to disable or enable it. Disabled
                        projects won&apos;t appear publicly.
                      </p>
                    </div>
                  </div>
                )}

                {activeInnerTab === "delete" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>Delete Project</h2>
                      <p className={styles.sectionDescription}>
                        Select and delete a project from the system
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === "portal" && (
              <>
                <div className={styles.innerTabNavigation}>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activePortalInnerTab === "create"
                        ? styles.activeInnerTab
                        : ""
                    }`}
                    onClick={() => setActivePortalInnerTab("create")}
                  >
                    Create Subscription Plan
                  </button>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activePortalInnerTab === "update"
                        ? styles.activeInnerTab
                        : ""
                    }`}
                    onClick={() => setActivePortalInnerTab("update")}
                  >
                    Update Subscription Plan
                  </button>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activePortalInnerTab === "disable"
                        ? styles.activeInnerTab
                        : ""
                    }`}
                    onClick={() => setActivePortalInnerTab("disable")}
                  >
                    Disable / Enable Subscription Plan
                  </button>
                  <button
                    type="button"
                    className={`${styles.innerTabButton} ${
                      activePortalInnerTab === "delete"
                        ? styles.activeInnerTab
                        : ""
                    }`}
                    onClick={() => setActivePortalInnerTab("delete")}
                  >
                    Delete Subscription Plan
                  </button>
                </div>

                {activePortalInnerTab === "create" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>
                        {t("createProjectPage.sections.subscriptionPlan.title")}
                      </h2>
                      <p className={styles.sectionDescription}>
                        {t(
                          "createProjectPage.sections.subscriptionPlan.description"
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {activePortalInnerTab === "update" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>
                        Update Subscription Plan
                      </h2>
                      <p className={styles.sectionDescription}>
                        Select and update an existing subscription plan
                      </p>
                    </div>
                  </div>
                )}

                {activePortalInnerTab === "disable" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>
                        Disable / Enable Subscription Plan
                      </h2>
                      <p className={styles.sectionDescription}>
                        Select a subscription plan to change its status.
                        Inactive plans won&apos;t appear publicly.
                      </p>
                    </div>
                  </div>
                )}

                {activePortalInnerTab === "delete" && (
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.sectionTitle}>
                        Delete Subscription Plan
                      </h2>
                      <p className={styles.sectionDescription}>
                        Select and delete a subscription plan from the system
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === "domain" && (
              <div className={styles.sectionHeader}>
                <div>
                  <h2 className={styles.sectionTitle}>
                    {t("createProjectPage.sections.addDomain.title")}
                  </h2>
                  <p className={styles.sectionDescription}>
                    {t("createProjectPage.sections.addDomain.description")}
                  </p>
                </div>
              </div>
            )}

            <div className={styles.formContainer}>
              {activeTab === "client" && (
                <>
                  {activeInnerTab === "create" && <ClientProjectForm />}
                  {activeInnerTab === "update" && <UpdateProjectForm />}
                  {activeInnerTab === "disable" && <DisableProjectForm />}
                  {activeInnerTab === "delete" && <DeleteProjectForm />}
                </>
              )}
              {activeTab === "portal" && (
                <>
                  {activePortalInnerTab === "create" && (
                    <PortalSubscriptionForm />
                  )}
                  {activePortalInnerTab === "update" && (
                    <UpdateSubscriptionPlanForm />
                  )}
                  {activePortalInnerTab === "disable" && (
                    <DisableSubscriptionPlanForm />
                  )}
                  {activePortalInnerTab === "delete" && (
                    <DeleteSubscriptionPlanForm />
                  )}
                </>
              )}
              {activeTab === "domain" && <AddDomainForm />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
